import React from 'react';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { Redirect } from 'react-router-dom';
import get from 'lodash/get';
import Home from './Home';
import Preview from './Preview';
import NotFound from './NotFound';
import keyBy from 'lodash/keyBy';
import Navigation from './Navigations';
import Footer from './Footer';
import FeaturedProjects from './FeaturedProjects';
import SingleProject from './SingleProject';
import Wrapper from './Wrapper';
import Loader from './Loader';
import ReactCSSTransitionGroup from 'react-addons-css-transition-group';
import { Predicates } from 'prismic.io';
import filter from 'lodash/filter';
import map from 'lodash/map';
import Helmet from 'react-helmet';

//Must be at the end so it go through the Prismic middleware because ended up in the final route
import I18N from '../i18n';

//provide a lang parameter in the route
export const I18NUrl = urlPart => {
  return `/:lang(${map(I18N.languages, (value, key) => key).join('|')})${urlPart || ''}`
}

export const I18NConfig = (match, options) => {
  return Object.assign(options || {}, { lang: I18N.languages[match.params.lang].key });
}

class App extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      site: {
        title: null,
        sections: [],
        description: null,
      },
      projects: {},
      isLoading: true,
      mainHeight: null,
      navAltColor: false,
    };

    this.currentNavAltColor = false;

    this.fetchData = this.fetchData.bind(this);
    this.mainHeight = this.mainHeight.bind(this);
    this.getColor = this.getColor.bind(this);
    this.getProject = this.getProject.bind(this);
    this.setNavAltColor = this.setNavAltColor.bind(this);
    this.I18NConfig = this.I18NConfig.bind(this);
  }

  I18NConfig(options) {
    return Object.assign(options || {}, { lang: this.props.match.params.lang });
  }

  componentDidMount() {
    this.fetchData();
    window.addEventListener('scroll', this.mainHeight);
  }

  mainHeight() {
    const main = this.main;

    if (!main) {
      return;
    }

    const mainHeight = Math.max(
      main.clientHeight,
      main.scrollHeight,
      main.offsetHeight
    );

    this.setState({
      mainHeight,
    });
  }

  fetchData() {
    // Query homepage
    this.setState({
      isLoading: true,
    });

    console.log(this.props);

    this.props.prismicCtx.api
      .getSingle('home', I18NConfig(this.props.match))
      .then(home => {
        // if a document is returned...
        if (home) {
          const sections = home.getGroup(
            'home.sections',
            I18NConfig(this.props.match)
          );

          const featuredProjects = home.getGroup(
            'home.featured_projects',
            I18NConfig(this.props.match)
          );

          const featured = featuredProjects && filter(featuredProjects.toArray().map(featured => {
            const project = featured.get('project', I18NConfig(this.props.match));

            return (project && project.uid) || null;
          }), a => (a !== null));

          const site = {
            title: home.getText('home.title'),
            homeTitle: home.getText('home.home_title'),
            sections: (sections && sections.toArray()) || [],
            description: home.getStructuredText(
              'home.description'),
            featured: featured,
          };

          this.props.prismicCtx.api
            .query(Predicates.at('document.type', 'project'), {
              pageSize: 100,
              ...I18NConfig(this.props.match),
            })
            .then(response => {
              // If a document is returned, update app state...
              if (response) {
                this.setState({
                  site,
                  projects: {
                    ...this.state.projects,
                    ...keyBy(response.results, o => o.uid),
                  },
                  isLoading: false,
                });
              }
            });
        }
      });
  }

  getColor(id) {
    if (!id) {
      return false;
    }

    const color =
      this.state.projects[id] &&
      this.state.projects[id].getColor('project.color');

    return color || false;
  }

  getProject(id) {
    const { projects } = this.state;
    return get(projects, id, null);
  }

  setNavAltColor(navAltColor = undefined) {
    if (typeof navAltColor === 'undefined') {
      return this.state.navAltColor;
    }

    this.setState({
      navAltColor: !!navAltColor,
    });
  }

  render() {
    const description = I18N.messages.description[this.props.match.params.lang];

    return (
      <div>
        <Helmet>
          <meta
            name="description"
            content={description}
          />
          <meta
            property="og:description"
            content={description}
          />
        </Helmet>
        <Loader loading={this.state.isLoading} />
        <div className="content" role="application">
          <main id="main" className="main" ref={main => (this.main = main)}>
            <ReactCSSTransitionGroup
              transitionName="fade"
              transitionEnterTimeout={500}
              transitionLeave={false}
            >
              <Switch
                key={document.location.pathname}
                location={document.location}
              >
                <Route
                  exact
                  path={I18NUrl('/')}
                  render={() => {
                    this.currentNavAltColor = false;

                    return (
                      <Wrapper color={false}>
                        <Home
                          site={this.state.site}
                          setNavAltColor={this.setNavAltColor}
                        />
                      </Wrapper>
                    );
                  }}
                />
                <Route
                  path={I18NUrl('/projects/:id')}
                  render={props => {
                    const id = props.match.params.id;

                    this.currentNavAltColor = true;
                    return (
                      <Wrapper color={this.getColor(id)}>
                        <SingleProject
                          project={this.getProject(id)}
                          {...props}
                          setNavAltColor={this.setNavAltColor}
                          site={this.state.site}
                          mainHeight={this.state.mainHeight}
                        />
                      </Wrapper>
                    );
                  }}
                />
                <Route
                  exact
                  path="/preview"
                  render={routeProps =>
                    <Preview
                      {...routeProps}
                      prismicCtx={this.props.prismicCtx}
                    />}
                />
                <Route component={NotFound} />
              </Switch>
            </ReactCSSTransitionGroup>
            <FeaturedProjects
              projects={this.state.projects}
              featured={this.state.site.featured}
              currentLang={this.props.match.params.lang}
            />
          </main>
        </div>
        <Footer
          sections={this.state.site.sections}
          mainHeight={this.state.mainHeight}
          setNavAltColor={this.setNavAltColor}
          currentIsNavAltColor={this.currentNavAltColor}
        />
        <Navigation
          altColor={this.state.navAltColor}
          location={document.location}
          currentLang={this.props.match.params.lang}
        />
      </div>
    );
  }
}

class AppWrapper extends React.Component {
  render() {
    return (
      <Router>
        <div>
          <Route exact path="/" render={() => <Redirect to={I18N.default} />} />
          <Route
            path={I18NUrl('/')}
            render={({ match }) =>
              <App prismicCtx={this.props.prismicCtx} match={match} />}
          />
        </div>
      </Router>
    );
  }
}

export default AppWrapper;



// WEBPACK FOOTER //
// ./src/components/App.js